import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme,
} from '@material-ui/core';
import { makeStyles, alpha } from '@material-ui/core/styles';
import { useApi, fetchApiRef, configApiRef } from '@backstage/core-plugin-api';
import { Link as RouterLink } from 'react-router-dom';
import { formatInstantInUserTimezone } from '../../utils/formatInstantInUserTimezone';
import {
  filterAdminTickets,
  type AdminTicketSortBy,
  type AdminTicketStatusFilter,
} from '../../utils/adminTicketFilters';
import {
  effectiveRequestStatus,
  requestDocIndicatesRejected,
  requestDocIndicatesUserCancelled,
} from '../../utils/requestRejection';
import type { RequestDoc } from './requestDoc';

const LIMIT_OPTIONS = [10, 20, 50, 100];
const FILTER_DEBOUNCE_MS = 300;

function errorHintForAdminTicketsLoad(error: string): string | undefined {
  if (error === 'Only administrators can view all tickets.') {
    return undefined;
  }
  if (/HTTP 5\d\d/.test(error)) {
    return 'Server error. Check backend logs for details.';
  }
  if (error === 'Failed to fetch' || /network/i.test(error)) {
    return 'Network error. Check your connection and try again.';
  }
  return 'Check backend logs or try again in a moment.';
}

const useRowStyles = makeStyles(theme => ({
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
    },
  },
}));

const STATUS_FILTERS: { value: AdminTicketStatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in-progress', label: 'In progress' },
  { value: 'closed', label: 'Closed' },
];

export function AdminAllTicketsPanel() {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const theme = useTheme();
  const rowStyles = useRowStyles();

  const [limit, setLimit] = useState(50);
  const [tickets, setTickets] = useState<RequestDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<AdminTicketStatusFilter>('all');
  const [createdDate, setCreatedDate] = useState('');
  const [requestedBy, setRequestedBy] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedRequestedBy, setDebouncedRequestedBy] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<AdminTicketSortBy>('created');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const id = window.setTimeout(
      () => setDebouncedRequestedBy(requestedBy),
      FILTER_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(id);
  }, [requestedBy]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search), FILTER_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [search]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const baseUrl = config.getString('backend.baseUrl');
      const url = `${baseUrl}/api/requests/requests/operator/all-tickets?limit=${limit}`;
      const res = await fetchApi.fetch(url, { credentials: 'include' });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        if (res.status === 403) {
          throw new Error('Only administrators can view all tickets.');
        }
        if (res.status >= 500) {
          const detail = body.trim();
          throw new Error(detail ? `${detail} (HTTP ${res.status})` : `HTTP ${res.status}`);
        }
        throw new Error(body.trim() || `HTTP ${res.status}`);
      }
      const data = (await res.json()) as { requests: RequestDoc[] };
      setTickets(data.requests ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tickets');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [config, fetchApi, limit]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const displayed = useMemo(
    () =>
      filterAdminTickets({
        tickets,
        statusFilter,
        createdDate,
        requestedBy: debouncedRequestedBy,
        search: debouncedSearch,
        sortBy,
        sortDir,
      }),
    [tickets, statusFilter, createdDate, debouncedRequestedBy, debouncedSearch, sortBy, sortDir],
  );

  const handleSort = (property: AdminTicketSortBy) => {
    const isActive = sortBy === property;
    const isAsc = isActive && sortDir === 'asc';
    setSortDir(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    const errorHint = errorHintForAdminTicketsLoad(error);
    return (
      <Box p={2}>
        <Typography color="error">{error}</Typography>
        {errorHint ? (
          <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 4 }}>
            {errorHint}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        All tickets across users (newest first from server, up to {limit} open and {limit}{' '}
        closed). Use filters below to narrow by status, date, requester, or search text.
      </Typography>

      <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginBottom: 16 }}>
        <FormControl size="small" style={{ minWidth: 120 }}>
          <InputLabel id="admin-tickets-limit-label">Show (per status)</InputLabel>
          <Select
            labelId="admin-tickets-limit-label"
            value={limit}
            label="Show (per status)"
            onChange={e => {
              const v = Number(e.target.value);
              if (LIMIT_OPTIONS.includes(v)) setLimit(v);
            }}
          >
            {LIMIT_OPTIONS.map(n => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          variant="outlined"
          label="Created on date"
          type="date"
          value={createdDate}
          onChange={e => setCreatedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'aria-label': 'Created on date' }}
          helperText="Uses your local timezone"
          style={{ minWidth: 180 }}
        />

        <TextField
          size="small"
          variant="outlined"
          label="Requested by (email or name)"
          value={requestedBy}
          onChange={e => setRequestedBy(e.target.value)}
          inputProps={{ 'aria-label': 'Requested by (email or name)' }}
          style={{ minWidth: 240 }}
        />

        <TextField
          size="small"
          variant="outlined"
          label="Search tickets"
          placeholder="ID, name, or requester"
          value={search}
          onChange={e => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Search tickets' }}
          style={{ minWidth: 240 }}
        />
      </Box>

      <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8, marginBottom: 16 }}>
        <Typography variant="body2" color="textSecondary">
          Status:
        </Typography>
        {STATUS_FILTERS.map(s => (
          <Chip
            key={s.value}
            label={s.label}
            clickable
            color={statusFilter === s.value ? 'primary' : 'default'}
            variant={statusFilter === s.value ? 'default' : 'outlined'}
            onClick={() => setStatusFilter(s.value)}
          />
        ))}
      </Box>

      {displayed.length === 0 ? (
        <Box p={3}>
          <Typography color="textSecondary">No tickets match the current filters.</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Request ID</TableCell>
                <TableCell>Request name</TableCell>
                <TableCell>Requested by</TableCell>
                <TableCell>Status</TableCell>
                <TableCell sortDirection={sortBy === 'created' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'created'}
                    direction={sortBy === 'created' ? sortDir : 'asc'}
                    onClick={() => handleSort('created')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'last_updated' ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === 'last_updated'}
                    direction={sortBy === 'last_updated' ? sortDir : 'asc'}
                    onClick={() => handleSort('last_updated')}
                  >
                    Last updated
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayed.map(r => (
                <TableRow
                  key={r.request_id}
                  hover
                  className={rowStyles.clickableRow}
                  onClick={() =>
                    window.open(
                      `${window.location.origin}/requests/${r.request_id}`,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  <TableCell>
                    <RouterLink
                      to={`/requests/${r.request_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: theme.palette.primary.main,
                        fontWeight: 600,
                        textDecoration: 'underline',
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      {r.request_id}
                    </RouterLink>
                  </TableCell>
                  <TableCell>{r.request_name}</TableCell>
                  <TableCell>{r.requested_by}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 6 }}>
                      <Chip
                        label={effectiveRequestStatus(r)}
                        size="small"
                        color={effectiveRequestStatus(r) === 'closed' ? 'default' : 'primary'}
                        variant="outlined"
                      />
                      {requestDocIndicatesRejected(r) ? (
                        <Chip label="Rejected" size="small" color="secondary" variant="outlined" />
                      ) : null}
                      {requestDocIndicatesUserCancelled(r) ? (
                        <Chip label="Cancelled" size="small" color="secondary" variant="outlined" />
                      ) : null}
                    </Box>
                  </TableCell>
                  <TableCell>{formatInstantInUserTimezone(r.audit?.created_at)}</TableCell>
                  <TableCell>{formatInstantInUserTimezone(r.audit?.last_updated_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
        Showing {displayed.length} of {tickets.length} loaded tickets.
      </Typography>
    </Box>
  );
}
